import { QuerySelectExpr } from '@apis/Customers/model';
import { postNotificationEvaluateAlertCondition } from '@apis/Notification';
import {
    NotificationComponentConfig,
    NotificationDefinitionPresentationOptions,
    ScheduleDefinition,
    UserDefinedNotificationConfig,
    UserDefinedNotificationQueryOptions,
    UserDefinedNotificationThreshold,
    UserDefinedNotificationTrigger,
} from '@apis/Notification/model';
import { QueryConstant, QueryExpr, QueryOperation } from '@apis/Resources';
import { UserConfiguredBarChartSettings } from '@root/Components/Charts/BarChart';
import { ChartTypes } from '@root/Components/Charts/Common';
import { GridChartSettings } from '@root/Components/Charts/GridChart';
import { ChartConfig } from '@root/Components/DashboardLayout/Charts/ChartRenderer';
import { createDatasourceSchemaContext, DatasourceSchemaContext } from '@root/Components/Filter/DatasourceContext';
import { FilterSettings } from '@root/Components/Settings/FilterSettings';
import { getInputSpec, ICustomInputSpec, IInputSpecBuilderType, InputSpecBuilder } from '@root/Components/Settings/InputSpec';
import { FormElement, FormSection, FormSectionItem } from '@root/Components/Settings/SettingsForms';
import { RichTextRun } from '@root/Components/Text/RichText';
import { RequestThrottlingService } from '@root/Services/AsyncBundler';
import { EventEmitter } from '@root/Services/EventEmitter';
import { FormatService } from '@root/Services/FormatService';
import { QueryDatasource, QueryDatasourceMetadata } from '@root/Services/Query/QueryDatasource';
import { exprBuilder, SchemaService, TFluentOps } from '@root/Services/QueryExpr';
import { addDays, addMonths, startOfDay } from 'date-fns';
import { ComponentPropsWithoutRef, ReactNode } from 'react';
import { BellRinging, Report, Settings } from 'tabler-icons-react';
import { IPresetBehavior, IPresetItem, IPresetOption } from '../../../Settings/Presets';
import { AlertSettings } from '../../Components/Alert';
import { RecurrenceRuleType, ScheduleSettings } from '../../Components/Schedule';
import { ChartSettingsInput } from './ChartSettingsInput';

// Core Types

export type INotificationPresetOption<T extends TypedNotificationConfig> = IPresetOption<UserDefinedNotificationConfig, T, unknown>;
export type INotificationPresetItem<T extends TypedNotificationConfig> = IPresetItem<UserDefinedNotificationConfig, T, unknown>;

type INotificationPresetBehavior<T extends TypedNotificationConfig> = IPresetBehavior<UserDefinedNotificationConfig, T, unknown>;
export interface NotificationConfigPresentation {
    presetId?: string;
}

export type PrefixedParams<T> = { [K in keyof T as `p${Capitalize<string & K>}`]: T[K] };

type ParamQueryExprs<T> = { [K in keyof T]: T[K] extends QueryOperation ? T[K] : QueryConstant<T[K]> };

type TPrms = Record<string, QueryExpr>;
interface TCfg extends NotificationComponentConfig {}

interface TypedNotificationConfig<TC extends TCfg = TCfg, TP extends TPrms = TPrms>
    extends Omit<UserDefinedNotificationConfig, 'ComponentConfig' | 'QueryOptions' | 'PresentationOptions'> {
    ComponentConfig: TC;
    QueryOptions: UserDefinedNotificationQueryOptions & { Parameters?: TP };
    PresentationOptions: NotificationConfigPresentation & NotificationDefinitionPresentationOptions;
}
type TypedParamConfig<TParams extends {}> = TypedNotificationConfig<{}, TParams>;

type StandardReportOptions<TC extends TCfg = TCfg, TP extends TPrms = TPrms> = {
    dataOptions?: (data: TypedNotificationConfig<TC, TP>, changedEvt: EventEmitter<TC>) => FormSectionItem;
    scheduleOverride?: (data: TypedNotificationConfig<TC, TP>) => FormSectionItem;
    scheduleFormType?: 'Daily' | 'Weekly' | 'Monthly';
    titleOverride?: string;
};

// Declarative Preset Definition

export interface PresetDefinition<T extends TypedNotificationConfig = TypedNotificationConfig> {
    idParts: Array<string | number | undefined | null>;
    label: string | Array<string | RichTextRun>;
    description: string;
    icon: JSX.Element;
    initialize: (data: UserDefinedNotificationConfig) => T;
    getForm: (data: T, helpers: PresetFormHelpers) => FormSection[];
    validate?: (data: T) => string[];
}

export interface PresetFormHelpers {
    chartInput: (data: TypedNotificationConfig, onChange?: (c: NotificationComponentConfig) => void) => FormSection;
    filterInput: (data: TypedNotificationConfig, onChange?: (c: any) => void) => ICustomInputSpec<QueryExpr[], any>;
    section: (title: string, ...elements: Array<FormElement | FormSectionItem>) => FormSection;
    sectionItem: (header: string | { label: string; controls?: ReactNode }, ...elements: FormElement[]) => FormSectionItem;
    descriptionInputs: (data: TypedNotificationConfig) => FormElement[];
    alertConditionSection: <D extends TypedNotificationConfig>(def: D, configChanged: EventEmitter<D['ComponentConfig']>) => FormSectionItem;
    scheduleForm: (schedule: ScheduleDefinition, type?: RecurrenceRuleType) => FormSectionItem;
    addDivider: (elements: FormElement[]) => void;
    concatSectionItems: (...elements: FormElement[][]) => FormElement[];
    input: <U>() => IInputSpecBuilderType<U>;
}

// Interfaces

export interface IDatasourcePresetsBuilder {
    getPresets(): INotificationPresetItem<any>[];
}
export interface IDatasourcePresetsBuilderFactory {
    getSupportedDatasources(): QueryDatasourceMetadata[];
    get(datasourceName: string): Promise<IDatasourcePresetsBuilder>;
}

// Base Builder

export abstract class BaseDatasourcePresetsBuilder implements IDatasourcePresetsBuilder {
    private readonly datasourceName: string;
    protected readonly schemaCtx: DatasourceSchemaContext;

    protected get fmtSvc() {
        return FormatService.instance;
    }

    public constructor(protected readonly baseQueryDatasource: QueryDatasource, private readonly schemaSvc: SchemaService) {
        this.datasourceName = baseQueryDatasource.name;
        this.schemaCtx = createDatasourceSchemaContext(schemaSvc, baseQueryDatasource);
    }

    // ─── Declarative preset registration ─────────────────────────────

    protected buildPreset<T extends TypedNotificationConfig>(def: PresetDefinition<T>): INotificationPresetItem<T> {
        const id = this.createId(...def.idParts);
        const helpers = this.createFormHelpers();

        const behavior: INotificationPresetBehavior<T> = {
            initialize: ({ data }) => def.initialize(data),
            getForm: ({ data }) => def.getForm(data as T, helpers),
            validate: def.validate ? ({ data }) => (def.validate as any)(data) : () => [],
        };

        return {
            id,
            label: def.label,
            icon: def.icon,
            description: def.description,
            behavior,
        };
    }

    protected buildCustomAlertPreset<T extends TypedNotificationConfig>(
        initialize: PresetDefinition<T>['initialize'],
        formOverride?: (data: T, helpers: PresetFormHelpers) => FormSection[]
    ): INotificationPresetItem<T> {
        return this.buildPreset({
            idParts: ['custom', 'alert'],
            label: 'Custom Alert',
            description: 'Custom alert with configurable data and alert conditions',
            icon: <Settings />,
            initialize,
            getForm: formOverride ?? ((data, h) => this.standardAlertForm(data, h)),
        });
    }

    protected buildCustomReportPreset<T extends TypedNotificationConfig>(
        initialize: PresetDefinition<T>['initialize'],
        formOverride?: (data: T, helpers: PresetFormHelpers) => FormSection[]
    ): INotificationPresetItem<T> {
        return this.buildPreset({
            idParts: ['custom', 'report'],
            label: 'Custom Report',
            description: 'Custom report with configurable data and schedule',
            icon: <Settings />,
            initialize,
            getForm: formOverride ?? ((data, h) => this.standardReportForm(data, h)),
        });
    }

    // ─── Standard form layouts ───────────────────────────────────────

    /** Standard alert form: chart settings + description + alert conditions. */
    protected standardAlertForm(
        data: TypedNotificationConfig,
        h: PresetFormHelpers,
        options?: { hideFilter?: boolean; collapsed?: boolean }
    ): FormSection[] {
        const configChanged = new EventEmitter(data.ComponentConfig!);
        const chartSettings = h.chartInput(data, configChanged.emit);
        if (options?.collapsed) chartSettings.collapsed = true;

        return [
            chartSettings,
            h.section(
                'Alert Settings',
                h.sectionItem('Title and Description', ...h.descriptionInputs(data)),
                h.alertConditionSection(data, configChanged)
            ),
        ];
    }

    /** Standard report form: chart settings + description + schedule. */
    protected standardReportForm(
        data: TypedNotificationConfig,
        h: PresetFormHelpers,
        options?: { scheduleType?: RecurrenceRuleType }
    ): FormSection[] {
        const chartSettings = h.chartInput(data);
        return [
            chartSettings,
            h.section(
                'Report Settings',
                h.sectionItem('Title and Description', ...h.descriptionInputs(data)),
                h.scheduleForm(data.Trigger?.Schedule!, options?.scheduleType)
            ),
        ];
    }

    // Form helpers factory

    private createFormHelpers(): PresetFormHelpers {
        const chartBehavior = this.createCustomChartBehavior(false);
        const chartBehaviorHidden = this.createCustomChartBehavior(true);
        const filterSettings = this.createFilterSettings();

        return {
            chartInput: (data, onChange) => chartBehavior(data, onChange),
            filterInput: (data, onChange) => filterSettings(data, onChange),
            section: this.createSection.bind(this),
            sectionItem: this.createSectionItem.bind(this),
            descriptionInputs: this.createDescriptionInputs.bind(this),
            alertConditionSection: this.createAlertConditionSection.bind(this),
            scheduleForm: this.createScheduleForm.bind(this),
            addDivider: this.addDivider.bind(this),
            concatSectionItems: this.concatSectionItems.bind(this),
            input: InputSpecBuilder.create,
        };
    }

    // #region Presets

    public getPresets() {
        return [] as INotificationPresetItem<any>[];
    }

    protected createDatasourcePresets(
        name: string,
        icon: JSX.Element,
        alerts: INotificationPresetItem<any>[],
        reports: INotificationPresetItem<any>[]
    ) {
        const root: INotificationPresetItem<any> = {
            label: name,
            icon: icon,
            children: [],
        };

        if (alerts.length) {
            root.children!.push({
                label: 'Alerts',
                icon: <BellRinging />,
                children: alerts,
            });
        }

        if (reports.length) {
            root.children!.push({
                label: 'Scheduled Reports',
                icon: <Report />,
                children: reports,
            });
        }

        return [root];
    }

    protected createId(...idParts: Array<string | number | undefined | null>) {
        return `${this.datasourceName}-${idParts.map((v) => v ?? 'n').join('-')}`;
    }

    // #endregion

    // #region Legacy Builder Helpers

    protected createCustomReportPreset<T extends TypedNotificationConfig>(behavior: INotificationPresetBehavior<T>) {
        const id = this.createId('custom', 'report');
        return {
            id,
            label: 'Custom Report',
            icon: <Settings />,
            description: 'Custom report with configurable data and schedule',
            behavior,
        };
    }

    protected createCustomAlertPreset<T extends TypedNotificationConfig>(behavior: INotificationPresetBehavior<T>) {
        const id = this.createId('custom', 'alert');
        return {
            id,
            label: 'Custom Alert',
            icon: <Settings />,
            description: 'Custom alert with configurable data and alert conditions',
            behavior,
        };
    }

    protected createPresetLabel(...parts: Array<string | number | { value?: string | number; placeholder?: string; suffix?: string }>) {
        const result: Array<string | RichTextRun> = [];
        for (const part of parts) {
            const { value, placeholder, suffix } = typeof part === 'object' ? part : { value: part, placeholder: '', suffix: '' };
            const valueDefined = value !== undefined && value !== null;
            const text = !valueDefined ? placeholder ?? 'X' : value!.toString();
            let separator = ' ';
            if (suffix) {
                separator = suffix + ' ';
            }
            const item: string | RichTextRun = !valueDefined
                ? { text, type: 'variable' }
                : typeof part === 'string'
                ? text
                : { text, type: 'explicit' };
            result.push(item, separator);
        }
        return result;
    }

    protected createPresetDescription(...parts: Array<string | number | { value?: string | number; placeholder?: string; suffix?: string }>) {
        const result: string[] = [];
        for (const part of parts) {
            const { value, placeholder, suffix } = typeof part === 'object' ? part : { value: part, placeholder: '', suffix: '' };
            const rawText = value === undefined || value === null ? placeholder ?? 'configured' : value.toString();
            const text = suffix ? rawText + suffix : rawText;
            result.push(text);
        }
        return result.join(' ');
    }

    // #endregion

    // #region Forms

    protected createDescriptionInputs(data: TypedNotificationConfig): FormElement[] {
        const isAlert = data.Trigger?.TriggerType === 'Threshold';
        return [
            getInputSpec(data, 'string', (d) => d.Title, {
                label: 'Title',
                presentationOptions: { fullWidth: true },
            }),
            getInputSpec(data, 'string-multiline', (d) => d.Description, {
                label: !isAlert ? 'Report Description' : 'Action Item',
                presentationOptions: {
                    fullWidth: true,
                    placeholder: isAlert
                        ? `Reminder or action item, e.g., "This alert indicates that X has occurred and requires your attention."`
                        : 'Reminder or description, e.g., "This report contains the latest sales data for your review."',
                },
            }),
        ];
    }

    protected createStandardAlertBehavior<TResult extends TypedNotificationConfig>(
        filterOverride?: () => FormSectionItem,
        dataOptionCollapsed?: boolean
    ): IPresetBehavior<INotificationPresetBehavior<any>, TResult, unknown>['getForm'] {
        const chartInput = this.createCustomChartBehavior(!!filterOverride, dataOptionCollapsed);
        return ({ data }) => {
            const configChanged = new EventEmitter(data.ComponentConfig!);

            const chartSettings = chartInput(data, configChanged.emit);
            if (filterOverride) {
                chartSettings.elements.push(filterOverride());
            }

            return [
                chartSettings,
                this.createSection(
                    'Alert Settings',
                    this.createSectionItem('Title and Description', ...this.createDescriptionInputs(data)),
                    this.createAlertConditionSection(data, configChanged)
                ),
            ];
        };
    }

    protected createStandardReportBehavior<TC extends TCfg, TP extends TPrms>(
        options: StandardReportOptions<TC, TP>
    ): IPresetBehavior<UserDefinedNotificationConfig, TypedNotificationConfig<TC, TP>, unknown>['getForm'] {
        const { dataOptions, scheduleOverride, scheduleFormType, titleOverride } = options;
        const filterInputs = this.createFilterSettings<TC>();
        return ({ data }) => {
            const configChanged = new EventEmitter(data.ComponentConfig);

            const dataOptionSectionItem = dataOptions?.(data, configChanged) ?? this.createSectionItem('Data Options');
            if (dataOptionSectionItem.elements.length) {
                this.addDivider(dataOptionSectionItem.elements);
            }
            dataOptionSectionItem.elements.push(filterInputs(data, configChanged.emit));

            return [
                this.createSection(
                    titleOverride ?? 'Report Settings',
                    this.createSectionItem('Title and Description', ...this.createDescriptionInputs(data)),
                    dataOptionSectionItem,
                    scheduleOverride?.(data) ?? this.createScheduleForm(data.Trigger?.Schedule!, scheduleFormType)
                ),
            ];
        };
    }

    // #endregion

    // #region Chart config

    protected createBaseBarChartConfig(groups: QuerySelectExpr[], values: QuerySelectExpr[], settings: UserConfiguredBarChartSettings) {
        return {
            Type: 'Chart',
            ...({
                type: 'chart',
                title: '',
                chartType: 'bar',
                datasourceName: this.datasourceName,
                groups,
                values,
                settings,
            } as ChartConfig),
        } as NotificationComponentConfig;
    }

    protected createCustomChartConfig(chartType: ChartTypes, dimFld?: QuerySelectExpr, metricFld?: QuerySelectExpr) {
        return {
            Type: 'Chart',
            ...({
                title: '',
                chartType,
                datasourceName: this.datasourceName,
                type: 'chart',
                filters: [],
                groups: dimFld ? [dimFld] : [],
                values: metricFld ? [metricFld] : [],
                settings: {},
            } as ChartConfig),
        } as NotificationComponentConfig;
    }

    protected createGridChartConfig(...columns: Array<GridChartSettings['columns'][0] & { width?: number; sortDir?: 'Asc' | 'Desc' }>) {
        return {
            ...this.createCustomChartConfig('grid'),
            settings: {
                columns,
                state: {
                    columns: columns.map((c) => ({ id: c.id, width: c.width ?? 150 })),
                    filters: [],
                    sort: columns.filter((c) => !!c.sortDir).map((c) => ({ Expr: c.select.Expr, Direction: c.sortDir! })),
                    groupBy: [],
                },
                allowFilter: false,
            } as GridChartSettings,
        };
    }

    protected createCustomChartBehavior(hideFilter: boolean, collapsed: boolean = false) {
        return (data: TypedNotificationConfig, onChange?: (componentConfig: NotificationComponentConfig) => void) => {
            const schemaCtx = this.getConfiguredSchemaCtx(data);
            const chartSettings: FormElement = {
                type: 'custom',
                value: data.ComponentConfig,
                component: ChartSettingsInput.buildForDatasource(schemaCtx, hideFilter),
                onChange: (v) => {
                    data.ComponentConfig = v;
                    onChange?.(v);
                },
            };

            const result = this.createSection('Data Options', chartSettings);
            if (collapsed) {
                result.collapsed = true;
            }
            return result;
        };
    }

    protected createFilterSettings<TC extends TCfg = TCfg>() {
        type CreateInput = (config: TC) => void;
        const filterInput = (data: TypedNotificationConfig, onChange?: CreateInput) => {
            const config = (data.ComponentConfig ??= { filters: [] } as { filters: QueryExpr[] });
            const schemaCtx = this.getConfiguredSchemaCtx(data);

            return {
                type: 'custom',
                onChange: (filters) => {
                    config.filters = filters;
                    onChange?.(config as TC);
                },
                component: FilterSettings,
                get value() {
                    return (config.filters ??= []) as QueryExpr[];
                },
                props: {
                    schemaCtx,
                },
            } as ICustomInputSpec<QueryExpr[], ComponentPropsWithoutRef<typeof FilterSettings>>;
        };
        return filterInput;
    }

    // #endregion

    // #region Form helpers ────────────────────────────────────────────────

    protected addDivider(elements: FormElement[]) {
        if (elements.length) {
            elements.push({ type: 'divider' });
        }
    }

    protected concatSectionItems(...elements: FormElement[][]) {
        return elements.reduce((acc, el) => {
            this.addDivider(acc);
            return acc.concat(el);
        }, []);
    }

    protected createScheduleForm(schedule: ScheduleDefinition, type?: RecurrenceRuleType) {
        type SchedProps = { value: ScheduleDefinition; onChange: (value: ScheduleDefinition) => void };
        return this.createSectionItem('Schedule Options', {
            type: 'custom',
            component: function ScheduleSettingsWrapper({ onChange }: SchedProps) {
                return <ScheduleSettings definition={schedule} typeConstraint={type} onChange={onChange} occurrencesNoun="reports" />;
            },
            onChange: () => {},
            value: schedule,
        });
    }

    protected createAlertConditionSection<T extends TypedNotificationConfig>(def: T, configChanged: EventEmitter<T['ComponentConfig']>) {
        type AlertProps = { value: UserDefinedNotificationThreshold; onChange: (value: UserDefinedNotificationThreshold) => void };
        const schemaCtx = this.getConfiguredSchemaCtx(def);
        const componentConfig = def.ComponentConfig!;
        const threshold = def.Trigger?.Threshold!;
        const reqThrottleSvc = RequestThrottlingService.create(500);
        let lastQueryOptionsKey = JSON.stringify(def.QueryOptions);
        const sendAlertEval = (dataSnapshot?: string) => {
            const nextQueryOptionsKey = JSON.stringify(def.QueryOptions);
            if (lastQueryOptionsKey !== nextQueryOptionsKey) {
                lastQueryOptionsKey = nextQueryOptionsKey;
                dataSnapshot = undefined;
            }
            const req = { Config: def, DataSnapshot: dataSnapshot };
            return postNotificationEvaluateAlertCondition(req);
        };
        const onEvalAlert = (dataSnapshot?: string) => {
            const reqKey = JSON.stringify(def);
            return reqThrottleSvc.throttle(reqKey, () => sendAlertEval(dataSnapshot));
        };
        return this.createSectionItem('Alert Conditions', {
            type: 'custom',
            component: function AlertConditionsWrapper({ onChange }: AlertProps) {
                const props = { schemaCtx, componentConfig, onEvalAlert, threshold };
                return <AlertSettings {...props} onChange={onChange} configChanged={configChanged} />;
            },
            onChange: () => {},
            value: threshold,
        });
    }

    protected createSection(title: string, ...elements: Array<FormElement | FormSectionItem>): FormSection {
        return {
            title,
            elements,
        };
    }

    protected createSectionItem(header: string | { label: string; controls?: ReactNode }, ...elements: FormElement[]): FormSectionItem {
        return {
            header: typeof header === 'string' ? { label: header } : header,
            elements,
        };
    }

    protected createUserInputParams<TDefaults extends { [K in keyof TDefaults]: TDefaults[K] }>(
        config: UserDefinedNotificationConfig,
        defaults: TDefaults
    ) {
        return Object.fromEntries(
            Object.entries(defaults).map(([k, v]) => {
                return [k, { Value: v ?? config.QueryOptions?.Parameters?.[k]?.Value }];
            })
        ) as ParamQueryExprs<TDefaults>;
    }

    protected createInput<T>() {
        return InputSpecBuilder.create<T>();
    }

    protected selectExprs<T extends { [K in keyof T]: TFluentOps<any> }>(data: T): QuerySelectExpr[] {
        const { builder: xb } = exprBuilder<T>();
        return Object.entries(data).map(([k, v]) => ({ Alias: k, Expr: xb.resolve(v as any) }));
    }

    protected getExprs<T extends any[]>(fields: { [K in keyof T]: string }) {
        type TResult = { [K in keyof T]: TFluentOps<T[K]> };
        const { builder: xb } = exprBuilder<T>();
        return fields.map((f) => xb.fromExpr({ Field: f })) as TResult;
    }

    // #endregion

    // #region Config merging ──────────────────────────────────────────────

    protected initializeConfig<TParams extends {}>(
        rawConfig: undefined | UserDefinedNotificationConfig,
        presetId: string,
        config: Optional<TypedParamConfig<TParams>, 'PresentationOptions'>
    ) {
        rawConfig ??= {};
        return {
            ...rawConfig,
            ...config,
            PresentationOptions: { presetId, ...config.PresentationOptions },
        } as unknown as TypedParamConfig<TParams>;
    }

    protected initializeBahavior<TC extends TCfg, TP extends TPrms = {}>(
        presetId: string,
        component: TC,
        params: TP | undefined,
        defaultOptions: Omit<UserDefinedNotificationConfig, 'ComponentConfig'>
    ) {
        type T = TypedNotificationConfig<TC, TP>;
        return this.createBehavior<TC, TP>(({ data }) =>
            this.mergeConfig<T>(
                {
                    ...(defaultOptions as T),
                    QueryOptions: {
                        ...defaultOptions.QueryOptions,
                        Parameters: params,
                    },
                    ComponentConfig: component,
                    PresentationOptions: { presetId, ...defaultOptions.PresentationOptions },
                },
                data
            )
        );
    }

    protected createBehavior<TC extends TCfg = TCfg, TP extends TPrms = {}>(
        initializer: INotificationPresetBehavior<TypedNotificationConfig<TC, TP>>['initialize']
    ) {
        type T = ReturnType<typeof initializer>;
        type TInit = INotificationPresetBehavior<T>;
        const result = { initialize: initializer } as Partial<TInit>;
        const spec = this.createInput<T>();
        type TSpec = typeof spec;
        type TGetFormArgs = Parameters<TInit['getForm']>[0];
        type TGetForm = (args: TGetFormArgs) => FormSection[];
        type TAddForm = (args: TGetFormArgs, spec: TSpec) => FormSection[];
        const addForm = (formBuilder: TAddForm) => {
            result.getForm = (args) => formBuilder(args, spec);
            return {
                addValidation: (validator: TInit['validate']) => {
                    result.validate = validator;
                    return result as TInit;
                },
            };
        };
        const form = (formBuilder: TAddForm) => {
            result.getForm = (args) => formBuilder(args, spec);
            result.validate = () => [];
            return result as TInit;
        };
        const standardReport = (optionsBuilder: (args: TGetFormArgs, spec: TSpec) => StandardReportOptions<TC, TP>) => {
            return form((args, spec) => {
                const options = optionsBuilder(args, spec);
                return this.createStandardReportBehavior<TC, TP>(options)(args);
            });
        };
        return { addForm, form, standardReport };
    }

    protected mergeConfig<T extends TypedNotificationConfig>(defaultOptions: Partial<T>, data: UserDefinedNotificationConfig) {
        return {
            ...data,
            Title: data.Title ?? defaultOptions?.Title ?? 'Untitled',
            Description: data.Description ?? defaultOptions?.Description ?? '',
            CooldownIncrement: data.CooldownIncrement ?? defaultOptions?.CooldownIncrement ?? 1,
            CooldownPeriod: data.CooldownPeriod ?? defaultOptions?.CooldownPeriod ?? 'Daily',
            ComponentConfig: { ...defaultOptions.ComponentConfig, ...data.ComponentConfig } as T['ComponentConfig'],
            Trigger: this.mergeTrigger(defaultOptions.Trigger ?? {}, data.Trigger ?? {}),
            QueryOptions: this.mergeQueryOptions<T>(defaultOptions.QueryOptions ?? {}, (data.QueryOptions ?? {}) as T['QueryOptions']),
            PresentationOptions: { ...defaultOptions.PresentationOptions, ...data.PresentationOptions },
        } as T;
    }

    protected mergeQueryOptions<T extends TypedNotificationConfig>(defaultOptions: Partial<T['QueryOptions']>, custom: T['QueryOptions']) {
        return {
            ...custom,
            ApiParameters: {
                ...defaultOptions.ApiParameters,
                ...custom?.ApiParameters,
            },
            Parameters: {
                ...defaultOptions.Parameters,
                ...custom?.Parameters,
            },
            DatasourceName: custom?.DatasourceName ?? defaultOptions.DatasourceName,
            Filters: custom?.Filters ?? defaultOptions.Filters,
        } as T['QueryOptions'];
    }

    protected mergeTrigger(defaultTrigger: UserDefinedNotificationTrigger, data: UserDefinedNotificationTrigger) {
        return {
            TriggerType: data.TriggerType ?? defaultTrigger.TriggerType,
            Schedule: this.mergeScheduleOptions('Weekly', data.Schedule ?? defaultTrigger.Schedule ?? {}),
            Threshold: data.Threshold ?? defaultTrigger.Threshold,
        };
    }

    protected defaultQueryOptions(datasourceName: string) {
        return {
            Parameters: {},
            Filters: [] as QueryExpr[],
            DatasourceName: datasourceName,
        };
    }

    protected defaultAlertTrigger<T>(optionId?: string, model?: T): UserDefinedNotificationTrigger {
        const settings = optionId ? [{ settings: [{ optionId, model }] }] : [];
        return {
            TriggerType: 'Threshold',
            Threshold: {
                ThrottlePeriod: 'Daily',
                ThrottleFrequency: 1,
                PresentationOptions: { settings },
            },
        };
    }

    protected defaultSchedTrigger(defaultType: RecurrenceRuleType): UserDefinedNotificationTrigger {
        return {
            TriggerType: 'Schedule',
            Schedule: this.getDefaultScheduleOptions(defaultType),
        };
    }

    protected mergeScheduleOptions(type: RecurrenceRuleType, schedule: ScheduleDefinition): ScheduleDefinition {
        return {
            DefaultTimezoneId: FormatService.instance.getTzName(),
            OccurrenceRules: !schedule?.OccurrenceRules?.length ? [this.getScheduleDefaultsByType(type)] : schedule.OccurrenceRules,
        };
    }

    protected getDefaultScheduleOptions(type: RecurrenceRuleType): ScheduleDefinition {
        return {
            DefaultTimezoneId: FormatService.instance.getTzName(),
            OccurrenceRules: [this.getScheduleDefaultsByType(type)],
        };
    }

    protected getScheduleDefaultsByType(type: RecurrenceRuleType) {
        const eightAmOffsetMin = 8 * 60;
        return {
            Type: type,
            StartAt: startOfDay(new Date()).toISOString(),
            TimeOffsetsInMinutes: [eightAmOffsetMin],
            Increment: 1,
            EndAt: addMonths(startOfDay(new Date()), 6).toISOString(),
            Date: addDays(startOfDay(new Date()), 1).toISOString(),
            DaysOfMonth: [0],
            DaysOfWeek: [1],
            SkipWeekends: true,
        };
    }

    protected getConfiguredSchemaCtx(def: TypedNotificationConfig) {
        return this.schemaCtx;
    }

    // #endregion
}
